/**
 * Admissions360 isolated API client. Uses same auth token pattern.
 */
import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/education360/adm`;

const authed = axios.create({ baseURL: BASE });
authed.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("edu360.token");
  const tenant = localStorage.getItem("edu360.tenant_slug");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  if (tenant) cfg.headers["X-Tenant-Slug"] = tenant;
  return cfg;
});

const publicClient = axios.create({ baseURL: BASE });

export const admAPI = {
  // Public
  prospectus: (slug) => publicClient.get(`/public/prospectus/${slug}`),
  submitEnquiry: (payload) => publicClient.post(`/public/enquiries`, payload),
  startApplication: (payload) => publicClient.post(`/public/applications`, payload),
  loadApplicantPortal: (token) => publicClient.get(`/public/applications/token/${token}`),
  autosaveApplication: (token, payload) => publicClient.patch(`/public/applications/token/${token}`, payload),
  submitApplication: (token, payload = {}) => publicClient.post(`/public/applications/token/${token}/submit`, payload),
  uploadDocument: (token, payload) => publicClient.post(`/public/applications/token/${token}/documents`, payload),
  acceptOffer: (token, oid) => publicClient.post(`/public/applications/token/${token}/offers/${oid}/accept`),
  declineOffer: (token, oid) => publicClient.post(`/public/applications/token/${token}/offers/${oid}/decline`),

  // Authenticated
  kpis: () => authed.get(`/kpis`),
  enquiries: () => authed.get(`/enquiries`),
  updateEnquiry: (id, d) => authed.patch(`/enquiries/${id}`, d),
  listApplicants: (params) => authed.get(`/applicants`, { params }),
  getApplicant: (id) => authed.get(`/applicants/${id}`),
  updateApplicant: (id, d) => authed.patch(`/applicants/${id}`, d),
  changeStage: (id, stage, reason = "") => authed.post(`/applicants/${id}/stage`, { stage, reason }),

  createInterview: (id, d) => authed.post(`/applicants/${id}/interviews`, d),
  updateInterview: (id, d) => authed.patch(`/interviews/${id}`, d),
  createAssessment: (id, d) => authed.post(`/applicants/${id}/assessments`, d),
  updateAssessment: (id, d) => authed.patch(`/assessments/${id}`, d),
  createOffer: (id, d) => authed.post(`/applicants/${id}/offers`, d),
  updateOffer: (id, d) => authed.patch(`/offers/${id}`, d),
  enrol: (id, d = {}) => authed.post(`/applicants/${id}/enrol`, d),
  listMessages: (id) => authed.get(`/applicants/${id}/messages`),
  sendMessage: (id, d) => authed.post(`/applicants/${id}/messages`, d),
  listAudit: (applicantId) => authed.get(`/audit`, { params: applicantId ? { applicant_id: applicantId } : {} }),

  // AI
  aiSummary: (id) => authed.post(`/ai/summary/${id}`),
  aiMissing: (id) => authed.post(`/ai/missing/${id}`),
  aiRecommend: (id) => authed.post(`/ai/recommend/${id}`),
  aiDraftComm: (id, kind) => authed.post(`/ai/draft-comm/${id}`, { kind }),
  aiInterviewKit: (id) => authed.post(`/ai/interview-kit/${id}`),
  aiEssay: (id) => authed.post(`/ai/essay/${id}`),
  aiScholarship: (id) => authed.post(`/ai/scholarship/${id}`),
  aiExecBriefing: (period = "Weekly") => authed.post(`/ai/executive-briefing`, { period }),
  sendBriefingNow: (period = "Weekly") => authed.post(`/executive-briefing/send-now`, { period }),
  listBriefings: () => authed.get(`/ai/briefings`),

  // PDF URLs
  registerPdf: () => `${BASE}/reports/register.pdf`,
  offerRegisterPdf: () => `${BASE}/reports/offer-register.pdf`,
  interviewSchedulePdf: () => `${BASE}/reports/interview-schedule.pdf`,
  waitingListPdf: () => `${BASE}/reports/waiting-list.pdf`,
  executivePdf: () => `${BASE}/reports/executive.pdf`,
};

// Helper: download an authenticated PDF via fetch (since <a> tags don't carry headers)
export async function downloadAuthedPdf(url, filename) {
  const token = localStorage.getItem("edu360.token");
  const tenant = localStorage.getItem("edu360.tenant_slug");
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Tenant-Slug": tenant || "",
    },
  });
  if (!res.ok) throw new Error(`Failed (${res.status})`);
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
